import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import useBoundingClientRect from 'hooks/useBoundingClientRect';

const tipSize = 6;
const contentBorderWith = 1;

const StyledTooltip = styled.div`
  ${({ rect, align }) => `
    position: absolute;
    left:0;
    right:0;
    ${align === 'top' ? `top: ${rect.top}px;` : `top: ${rect.bottom}px;`}
  `}
  z-index: ${({ theme, zIndex }) => zIndex ?? theme.zIndex.tooltip};

  > .backdrop {
    background: transparent;
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
  }
  > .tip {
    border: ${tipSize}px solid transparent;
    left:${({ rect }) => `${rect.left + rect.width / 2 - tipSize}px`};
    position: absolute;
    z-index:2;

    ${({ align, offset, theme }) => (align === 'top'
    ? `
      top: ${-offset - tipSize - contentBorderWith}px;
      border-top-color: ${theme.palette.messages.info.background};
    `
    : `
      top: ${-offset - tipSize + contentBorderWith}px;
      border-bottom-color: ${theme.palette.messages.info.background};
    `)}
  }
  >.contentWrapper{
    position: absolute;
    ${({ align }) => (align === 'top' ? 'bottom: 0px;' : 'top: 0px;')};
    width: 100%;
    > .content {
      ${({
    theme, offset, align, overlapRect, horizontalMargins,
  }) => `
        ${overlapRect ? `
          width: ${overlapRect.width - horizontalMargins * 2 * theme.spacing}px;
          left:${overlapRect.left + horizontalMargins * theme.spacing}px;
        ` : `
            left: ${theme.spacing * 2}px;
            right: ${theme.spacing * 2}px;
        `}

        ${
  align === 'top'
    ? `bottom: ${offset + tipSize}px;`
    : `top: ${offset + tipSize}px;`
}`}
      border: ${contentBorderWith}px solid rgba(255,255,255);
      background-color: ${({ theme }) => theme.palette.messages.info.background};
      border-radius: 5px;
      color: ${({ theme }) => theme.palette.messages.info.contrastText};
      font-family: ${({ theme }) => theme.typography.fontFamily};
      font-size: 1rem;
      font-weight: 400;
      padding: ${({ theme }) => theme.spacing * 2}px;
      position: absolute;
      text-align: left;
    }
  }

`;
const BodyElement = document.body;

const Tooltip = ({
  className,
  onClose,
  children,
  anchorToRef,
  offset = 0,
  align = 'top',
  overlapElementRef,
  zIndex,
}) => {
  const classes = ['Tooltip', className];

  const rect = useBoundingClientRect(anchorToRef.current);
  const overlapRect = useBoundingClientRect(overlapElementRef?.current || document.querySelector('.SingleColumnLayout'));

  return (rect.top !== undefined && overlapRect.top !== undefined && ReactDOM.createPortal(
    <StyledTooltip
      rect={rect}
      overlapRect={overlapRect}
      offset={offset}
      className={classes.filter((c) => !!c).join(' ')}
      align={align}
      zIndex={zIndex}
      horizontalMargins={overlapElementRef?.current ? 0 : 1}
    >
      <div role="presentation" className="backdrop" onClick={onClose} />
      <div className="tip" />
      <div className="contentWrapper" role="presentation">
        <div
          role="presentation"
          className="content"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </StyledTooltip>,
    BodyElement,
  )) || null;
};

Tooltip.displayName = 'Tooltip';

Tooltip.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.node,
};

Tooltip.defaultProps = {
  className: null,
  onClose: null,
  children: null,
};

export default Tooltip;

export const TooltipTextWrapper = styled.div`
  line-height: 1.4;
  a {
    color: inherit;
    font-weight: 600;
  }
`;
